import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Input, List, Modal, Popconfirm, Space, Typography, message } from "antd";
import LoadingCard from "components/LoadingCard/LoadingCard";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import GlossaryTermForm from "./GlossaryTermForm";
import { describeSync } from "./syncState";
import {
  createTerm,
  deleteTerm,
  errorMessage,
  fetchGlossary,
  resyncGlossary,
  updateTerm,
} from "./glossaryApi";

const { Title, Text } = Typography;

/**
 * The bg -> en terms the DeepL assist button applies.
 *
 * DeepL translates "ветеринарен лекар" as "veterinary doctor" and "запазване на
 * час" as "reservation of an hour" — correct word-by-word, wrong for this
 * product. A glossary is the only lever DeepL offers for that, so this screen
 * is a flat list of pairs, matching what DeepL actually stores.
 *
 * The banner at the top exists because the sync is allowed to fail quietly: a
 * DeepL outage must not stop an editor saving a term, which means the list on
 * screen can be ahead of the glossary DeepL is using. Without the banner that
 * gap is invisible, and "the glossary is not working" would be indistinguishable
 * from "the term is wrong".
 *
 * A `List` rather than a `Table`, like the Redirects screen: two strings per
 * row, one of them Cyrillic, and a two-column table of them is unreadable on
 * the phone this admin is mostly used from.
 */
export default function TranslationGlossary() {
  const [terms, setTerms] = useState([]);
  const [sync, setSync] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      // Already sorted by term — the API orders the list.
      const data = await fetchGlossary();
      setTerms(data?.terms ?? []);
      setSync(data?.sync ?? null);
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isNew = editing != null && !editing._id;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return terms;
    return terms.filter(
      (term) =>
        term.source?.toLowerCase().includes(needle) || term.target?.toLowerCase().includes(needle)
    );
  }, [terms, search]);

  const state = describeSync(sync, terms.length);

  const openEditor = (term) => {
    setEditing(term ?? {});
    form.setFieldsValue({ source: term?.source ?? "", target: term?.target ?? "" });
  };

  const closeEditor = () => {
    setEditing(null);
    form.resetFields();
  };

  const submit = async (values) => {
    setIsSaving(true);
    try {
      const payload = { source: values.source.trim(), target: values.target.trim() };

      if (isNew) await createTerm(payload);
      else await updateTerm(editing._id, payload);

      message.success("Запазено");
      closeEditor();
      // Reloads the sync state too: the save just triggered a DeepL rebuild and
      // the banner is the only place its outcome shows.
      await load();
    } catch (error) {
      // A 409 from the unique constraint arrives here naming the term, which is
      // the message worth showing: it means the term is already defined.
      message.error(errorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async (term) => {
    try {
      await deleteTerm(term._id);
      message.success("Изтрито");
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    }
  };

  const syncNow = async () => {
    setIsSyncing(true);
    try {
      const result = await resyncGlossary();
      setSync(result ?? null);
      // The endpoint answers 200 whether or not DeepL cooperated, so the result
      // — not the status — decides what this says.
      if (result?.lastError) message.warning(result.lastError);
      else message.success("Речникът е синхронизиран с DeepL");
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 720, margin: "0 auto" }}>
      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Речник за преводи
        </Title>
        <Text type="secondary">
          Термини, които DeepL трябва да превежда по този начин. Прилагат се винаги, когато
          бутонът за превод превръща българския текст в английски.
        </Text>
      </Space>

      {isLoading && <LoadingCard />}

      {!isLoading && loadError && (
        <Alert
          type="error"
          showIcon
          message="Неуспешно зареждане на речника"
          description={loadError}
          action={<Button onClick={load}>Опитай отново</Button>}
        />
      )}

      {!isLoading && !loadError && (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Alert
            type={state.tone}
            showIcon
            message={state.message}
            description={
              (state.detail || state.at) && (
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  {state.detail && <Text type="secondary">DeepL отговори: {state.detail}</Text>}
                  {state.at && (
                    <Text type="secondary">
                      {state.state === "stale" ? "Последен опит" : "Последно изпратено"}: {state.at}
                    </Text>
                  )}
                </Space>
              )
            }
            action={
              <Button style={CONTROL} loading={isSyncing} onClick={syncNow}>
                Синхронизирай с DeepL
              </Button>
            }
          />

          <Button type="primary" block style={CONTROL} onClick={() => openEditor(null)}>
            Нов термин
          </Button>

          <Input.Search
            allowClear
            placeholder="Търси по термин"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={CONTROL}
          />

          <List
            bordered
            dataSource={visible}
            locale={{ emptyText: search ? "Няма съвпадение." : "Все още няма термини." }}
            renderItem={(term) => (
              <List.Item style={{ display: "block", padding: 16 }}>
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  {/* Terms can be long; one must wrap rather than widen the page. */}
                  <Text strong style={{ wordBreak: "break-word" }}>
                    {term.source}
                  </Text>
                  <Text type="secondary" style={{ wordBreak: "break-word" }}>
                    → {term.target}
                  </Text>

                  <Space wrap size={8}>
                    <Button style={CONTROL} onClick={() => openEditor(term)}>
                      Редактирай
                    </Button>
                    <Popconfirm
                      title="Сигурен ли си, че искаш да изтриеш този термин?"
                      description="DeepL отново ще превежда термина по свое усмотрение."
                      onConfirm={() => remove(term)}
                      okText="Да"
                      cancelText="Отказ"
                    >
                      <Button danger style={CONTROL}>
                        Изтрий
                      </Button>
                    </Popconfirm>
                  </Space>
                </Space>
              </List.Item>
            )}
          />
        </Space>
      )}

      <Modal
        open={editing != null}
        title={isNew ? "Нов термин" : "Редактиране на термин"}
        onCancel={closeEditor}
        onOk={form.submit}
        confirmLoading={isSaving}
        okText="Запази"
        cancelText="Отказ"
        destroyOnClose
      >
        <GlossaryTermForm form={form} onSubmit={submit} />
      </Modal>
    </div>
  );
}
